import * as JsonPatch from 'fast-json-patch';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { TIME_VALUES } from '~/Constants/Date.constants';
import { USER_SERVICE_URLS } from '~/Constants/Urls.constants';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { getLocalStorageAccessToken } from '~/Hooks';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import IUser from '~/Models/DTO/User.model';

export const useRequestUser = () => {
  const enabled = !!getLocalStorageAccessToken();
  const queryClient = useQueryClient();

  const { isLoading, error, data, refetch, isFetched } = useQuery(
    REQUEST_ACTIONS.GET_USER,
    async () =>
      (
        await requestReactQuery<{ user: IUser }>({
          url: USER_SERVICE_URLS.MY_ACCOUNT,
          config: { method: 'GET' },
        })
      ).user,
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      refetchInterval: TIME_VALUES.HOUR,
      enabled,
    },
  );

  const { error: updatingError, isLoading: updatingLoading, mutate: updateUser } = useMutation(
    async (userData: JsonPatch.Operation[]) => {
      const response = await requestReactQuery<{ user: IUser }>({
        url: USER_SERVICE_URLS.USER,
        data: JSON.stringify(userData),
        config: { method: 'PATCH' },
      });
      queryClient.invalidateQueries(REQUEST_ACTIONS.GET_USER);
      return response;
    },
  );

  return {
    userLoading: isLoading,
    userError: error as IErrorResponse,
    user: data,
    refetchUser: refetch,
    updatingError: updatingError as IErrorResponse,
    updatingLoading,
    updateUser,
    hasUserLoaded: isFetched,
  };
};
